import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldOff, Search, Plus, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_STYLES = {
  hard_bounce: { color: 'bg-red-100 text-red-700', label: 'Adres geçersiz' },
  complaint: { color: 'bg-orange-100 text-orange-700', label: 'Spam şikâyeti' },
  manual: { color: 'bg-slate-100 text-slate-700', label: 'Elle eklendi' },
  unsubscribe: { color: 'bg-blue-100 text-blue-700', label: 'Abonelikten çıktı' },
};

const Suppressions = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [resubscribe, setResubscribe] = useState(false);

  const fetchItems = useCallback(async (term = '') => {
    try {
      const { data } = await axios.get(`${API}/suppressions`, {
        params: term ? { search: term } : {},
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Engel listesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Arama yazarken her tuşta istek atmamak için bekletme
  useEffect(() => {
    const timer = setTimeout(() => fetchItems(search), 400);
    return () => clearTimeout(timer);
  }, [search, fetchItems]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/suppressions`, {
        email: newEmail.trim(),
        reason: 'manual',
      });
      toast.success(data.message);
      setNewEmail('');
      await fetchItems(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Adres eklenemedi');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    const item = removeTarget;
    if (!item) return;

    setRemoveTarget(null);
    try {
      const { data } = await axios.delete(
        `${API}/suppressions/${encodeURIComponent(item.email)}`,
        { params: { resubscribe } }
      );
      toast.success(data.message);
      await fetchItems(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Adres çıkarılamadı');
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#6366f1]" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">Engel Listesi</h1>
        <p className="text-[#64748b]">
          Bu adreslere hiçbir kampanyada mail gönderilmez
        </p>
      </div>

      {/* Neden önemli */}
      <div className="mb-6 flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Bu liste neden var?</p>
          <p>
            Var olmayan adreslere mail göndermeye devam etmek, e-posta sağlayıcınızın
            gözündeki itibarınızı düşürür. Bir süre sonra <strong>geçerli
            alıcılarınızın</strong> mailleri de spam klasörüne düşmeye başlar. Kalıcı
            olarak teslim edilemeyen adresler ve spam şikâyetleri buraya otomatik eklenir.
          </p>
        </div>
      </div>

      {/* Özet + elle ekleme */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldOff size={18} className="text-red-500" />
            <p className="text-sm font-medium text-[#64748b]">Engellenen Adres</p>
          </div>
          <p className="text-2xl font-bold text-[#0f172a]">{total}</p>
        </div>

        <form
          onSubmit={handleAdd}
          className="md:col-span-2 bg-white p-5 rounded-xl border border-[#e2e8f0]"
        >
          <label htmlFor="newEmail" className="block text-sm font-medium text-[#64748b] mb-2">
            Elle adres engelle
          </label>
          <div className="flex gap-2">
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="ornek@firma.com"
              className="flex-1 px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
            />
            <button
              type="submit"
              disabled={adding || !newEmail.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] text-white rounded-lg hover:bg-[#4f46e5] disabled:opacity-50 font-medium"
            >
              {adding ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              Ekle
            </button>
          </div>
        </form>
      </div>

      {/* Arama */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Adres ara..."
          className="w-full pl-10 pr-4 py-2.5 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
        />
      </div>

      {/* Liste */}
      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-[#e2e8f0]">
          <ShieldOff size={56} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-lg font-bold text-[#0f172a] mb-1">
            {search ? 'Eşleşen adres yok' : 'Engel listesi boş'}
          </h3>
          <p className="text-[#64748b] text-sm">
            {search
              ? 'Farklı bir arama deneyin.'
              : 'Teslim edilemeyen adresler ve spam şikâyetleri buraya otomatik eklenecek.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#334155]">Adres</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#334155]">Sebep</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#334155]">Detay</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#334155]">Tarih</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const style = REASON_STYLES[item.reason] || REASON_STYLES.manual;
                  return (
                    <tr key={item.email} className="border-b border-[#f1f5f9] last:border-0">
                      <td className="px-6 py-4 font-medium text-[#0f172a]">{item.email}</td>
                      <td className="px-6 py-4">
                        <span className={`${style.color} px-3 py-1 rounded-full text-xs font-bold`}>
                          {style.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#64748b] max-w-xs truncate" title={item.detail}>
                        {item.detail || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#64748b]">{formatDate(item.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => { setRemoveTarget(item); setResubscribe(false); }}
                          className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <Trash2 size={15} />
                          Çıkar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Adres engel listesinden çıkarılsın mı?"
        description={
          removeTarget
            ? removeTarget.email +
              (removeTarget.reason === 'hard_bounce'
                ? '\n\nDİKKAT: Bu adres "kalıcı olarak teslim edilemedi" diye engellendi. ' +
                  'Adres hâlâ geçersizse tekrar göndermek gönderim itibarınıza zarar verir.'
                : '')
            : ''
        }
        extra={
          <label className="flex items-start gap-2 text-sm text-[#334155] cursor-pointer">
            <input
              type="checkbox"
              checked={resubscribe}
              onChange={(e) => setResubscribe(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Bu kişi tekrar mail alabilsin
              <span className="block text-xs text-[#64748b]">
                İşaretlenmezse engelden çıkar ama &quot;abonelikten çıkmış&quot; kalır.
              </span>
            </span>
          </label>
        }
        confirmLabel="Çıkar"
        destructive
        onConfirm={handleRemove}
      />
    </div>
  );
};

export default Suppressions;
